import type { ChangeEvent } from 'react';
import { Box, Choicebox, SectionHeader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ObesiQQuestionnaireName } from '@common/customControls/bariatrics';
import type { ServiceCode } from '@common/enums';
import type { Process } from '@common/interfaces';
import type { IProcessQuestionnaireResponse } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import { obesiQTranslations } from '@common/customControls/bariatrics';
import { QuestionnaireName } from '@common/enums';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { MonitoringLoader } from '@frontend/components/pages/monitoring/MonitoringLoader';
import { getProcessQuestionnaireResponses } from '@frontend/services/api/processesApiService';
import { canReadBariPostQuestionnaireResponses } from '@frontend/services/authorizationService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IQuestionnaire } from './ObesiQCard';
import { ChoiceboxWithWarning } from '../ChoiceboxWithWarning';
import { ObesiQCard } from './ObesiQCard';

type ObesiQTrendsProps = Readonly<{
    process: Process<ServiceCode.BariPost>;
}>;

export const obesiQQuestionnaireNames: ObesiQQuestionnaireName[] = [
    QuestionnaireName.obesiQEatingBehavior,
    QuestionnaireName.obesiQSocialFunction,
    QuestionnaireName.obesiQPsychologicalFunction,
    QuestionnaireName.obesiQPhysicalFunction,
    QuestionnaireName.obesiQBodyImage,
    QuestionnaireName.obesiQSexualFunction,
];
const OBESI_Q_STATE = 'obesiQState';

export function ObesiQTrends(props: ObesiQTrendsProps): JSX.Element | null {
    const { process } = props;
    const abortSignal = useAbortSignal();
    const [canReadQuestionnaireResponses, setCanReadQuestionnaireResponses] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [questionnaires, setQuestionnaires] = useState<IQuestionnaire[]>(getInitialQuestionnaires());
    const checkedQuestionnaires = questionnaires.filter((questionnaire) => questionnaire.isChecked);
    const surgeryDate = process.processData?.datum_operatie;

    useEffect(() => {
        loadCanReadQuestionnaireResponses();
    }, [process]);

    useEffect(() => {
        if (canReadQuestionnaireResponses && surgeryDate) {
            loadQuestionnaires(surgeryDate);
        }
    }, [canReadQuestionnaireResponses, process, surgeryDate]);

    if (!canReadQuestionnaireResponses) {
        return null;
    }

    if (isLoading) {
        return <MonitoringLoader />;
    }

    return (
        <>
            <SectionHeader
                noGutterOnMobile={true}
                title="OBESI-Q trends"
            />
            {!surgeryDate ? (
                <EmptyListPlaceholder subtitle="Er zijn nog geen ObesiQ-gegevens voor deze patiënt" />
            ) : (
                <>
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={3}
                        mb={6}
                        p={[4, 0]}
                        sx={{ maxWidth: 700 }}
                    >
                        {questionnaires.map((questionnaire) => (
                            <Choicebox
                                checked={questionnaire.isChecked}
                                key={questionnaire.name}
                                label={computeChoiceboxLabel(questionnaire)}
                                onChange={(event) => handleChange(event, questionnaire.name)}
                            />
                        ))}
                    </Box>
                    {checkedQuestionnaires.length === 0 ? (
                        <EmptyListPlaceholder subtitle="Klik hierboven een domein aan om de bijbehorende trendgrafiek te zien" />
                    ) : (
                        checkedQuestionnaires.map((questionnaire) => (
                            <ObesiQCard
                                key={questionnaire.name}
                                processId={process.id}
                                questionnaire={questionnaire}
                                surgeryDate={surgeryDate}
                            />
                        ))
                    )}
                </>
            )}
        </>
    );

    function computeChoiceboxLabel(questionnaire: IQuestionnaire): JSX.Element | string {
        const label = obesiQTranslations[questionnaire.name].title;

        return questionnaire.hasWarning ? <ChoiceboxWithWarning label={label} /> : label;
    }

    async function loadCanReadQuestionnaireResponses(): Promise<void> {
        const canRead = await canReadBariPostQuestionnaireResponses(process.id, abortSignal);
        setCanReadQuestionnaireResponses(canRead);
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>, questionnaireName: QuestionnaireName): void {
        const nextQuestionnaires = [...questionnaires];
        const questionnaire = nextQuestionnaires.find(
            (questionnaireItem) => questionnaireItem.name === questionnaireName
        );
        if (questionnaire) {
            questionnaire.isChecked = event.target.checked;
        }
        setQuestionnaires(nextQuestionnaires);
    }

    function mapToQuestionnaires(processQuestionnaireResponses: IProcessQuestionnaireResponse[]): IQuestionnaire[] {
        const nextQuestionnaires = [...questionnaires];
        for (const processQuestionnaireResponse of processQuestionnaireResponses) {
            const questionnaire = nextQuestionnaires.find(
                (questionnaireItem) => questionnaireItem.name === processQuestionnaireResponse.questionnaireName
            );
            if (questionnaire) {
                const obesiQStateItem = processQuestionnaireResponse.items?.find((item) => item.text === OBESI_Q_STATE);
                const hasWarning = obesiQStateItem?.answer.value === 'notify';
                questionnaire.hasWarning = hasWarning;
                questionnaire.id = processQuestionnaireResponse.questionnaireId;
                questionnaire.isChecked = hasWarning;
            }
        }
        const hasNoWarnings = nextQuestionnaires.filter((questionnaire) => questionnaire.hasWarning).length === 0;
        if (hasNoWarnings) {
            nextQuestionnaires[0].isChecked = true;
        }

        return nextQuestionnaires;
    }

    async function loadQuestionnaires(definedSurgeryDate: Date | string): Promise<void> {
        setIsLoading(true);
        const query = {
            itemTexts: [OBESI_Q_STATE],
            latestQuestionnaire: true,
            questionnaireNames: obesiQQuestionnaireNames,
        };
        const { items } = await getProcessQuestionnaireResponses(process.id, query, abortSignal);
        const filteredItems = items.filter((item) => item.performedDateTime >= definedSurgeryDate);
        const nextQuestionnaires = mapToQuestionnaires(filteredItems);
        setQuestionnaires(nextQuestionnaires);
        setIsLoading(false);
    }

    function getInitialQuestionnaires(): IQuestionnaire[] {
        return obesiQQuestionnaireNames.map((name) => ({ hasWarning: false, isChecked: false, name }));
    }
}
